import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { HistoryModal } from '@/components/history/HistoryModal';
import { HistoryTable } from '@/components/history/HistoryTable';
import type { HistoryTab } from '@/components/history/types';
import { CardNo, MemberLink, PolicyLink, RecordLink } from '@/components/links/RecordLinks';
import { useDashboardAttentionQuery, useDashboardTransactionsQuery } from '@/api/query/dashboard';
import type { DashboardAttentionItem, DashboardTransaction } from '@/types';
import { formatCurrency, paymentTypeLabel } from '@/lib/payments';
import { formatDateDisplay } from '@/lib/dates';
import { formatMonthKey } from '@/lib/months';
import { cn, displayMemberNo } from '@/lib/utils';
import { SectionCard } from './SectionCard';

/** Rows shown on the dashboard card; View All shows the rest. */
const PREVIEW_ROWS = 8;

const KIND = {
  policy: { label: 'Policy', dot: 'bg-[#4369a0]' },
  loan: { label: 'Loan', dot: 'bg-[#5ec8b8]' },
  balance: { label: 'Balance', dot: 'bg-amber-600' },
} as const;

function KindTag({ kind }: { kind: keyof typeof KIND }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-gray-600">
      <span className={cn('h-2 w-2 rounded-sm', KIND[kind].dot)} />
      {KIND[kind].label}
    </span>
  );
}

const IdCell = ({ id }: { id: string }) => <span className="flex w-[13px] justify-center whitespace-nowrap tabular-nums text-gray-500 font-semibold">{id}</span>;

const attentionTab = (items: DashboardAttentionItem[]): HistoryTab => ({
  id: 'attention',
  label: 'Attention Required',
  empty: 'All caught up. Every policy and loan is paid this month.',
  columns: [
    { label: 'ID' },
    { label: 'Card No' },
    { label: 'Member' },
    { label: 'Policy' },
    { label: 'Details' },
    { label: 'Type' },
    { label: 'Amount Due', align: 'right' },
  ],
  rows: items.map((item) => ({
    key: `${item.policyNo}-${item.kind}`,
    cells: [
      <IdCell key="id" id={displayMemberNo({ id: item.memberId, memberNo: item.memberNo })} />,
      <CardNo key="card" value={item.cardNo} />,
      <MemberLink key="member" memberId={item.memberId} memberNo={item.memberNo} name={item.memberName} className="font-semibold text-gray-900" />,
      <PolicyLink key="policy" member={{ id: item.memberId, name: item.memberName, memberNo: item.memberNo }} policyNo={item.policyNo} />,
      <span key="note" className="text-gray-500">{item.note}</span>,
      <KindTag key="kind" kind={item.kind} />,
      <b key="amount" className="text-amber-900">{formatCurrency(item.amountDue)}</b>,
    ],
  })),
});

const transactionsTab = (items: DashboardTransaction[]): HistoryTab => ({
  id: 'transactions',
  label: 'Recent Transactions',
  empty: 'No payments recorded yet.',
  columns: [
    { label: 'ID' },
    { label: 'Card No' },
    { label: 'Member' },
    { label: 'Date' },
    { label: 'Policy / Loan' },
    { label: 'Type' },
    { label: 'Mode' },
    { label: 'Amount', align: 'right' },
  ],
  rows: items.map((item, index) => ({
    key: `${item.date}-${item.policyNo ?? item.memberId}-${index}`,
    cells: [
      <IdCell key="id" id={displayMemberNo({ id: item.memberId, memberNo: item.memberNo })} />,
      <CardNo key="card" value={item.cardNo} />,
      <MemberLink key="member" memberId={item.memberId} memberNo={item.memberNo} name={item.memberName} className="font-semibold text-gray-900" />,
      formatDateDisplay(item.date),
      <RecordLink key="ref" member={{ id: item.memberId, name: item.memberName, memberNo: item.memberNo }} refNo={item.kind === 'loan' ? item.loanNo : item.policyNo} />,
      <KindTag key="kind" kind={item.kind} />,
      <span key="mode" className="text-gray-500">{paymentTypeLabel(item.paymentType)}</span>,
      <b key="amount">{formatCurrency(item.amount)}</b>,
    ],
  })),
});

interface ListCardProps {
  preview: HistoryTab;
  fullTab: HistoryTab;
  description: string;
  modalSubtitle: string;
  loading: boolean;
  showAll: boolean;
  onShowAllChange: (open: boolean) => void;
}

/** Dashboard card with the first rows of a list and a View All modal for the full list. */
function ListCard({ preview, fullTab, description, modalSubtitle, loading, showAll, onShowAllChange }: ListCardProps) {
  return (
    <SectionCard
      title={preview.label}
      description={description}
      className="xl:col-span-6"
      action={
        <Button type="button" variant="link" className="h-auto p-0 text-[#4369a0]" onClick={() => onShowAllChange(true)}>
          View All
        </Button>
      }
    >
      <HistoryTable columns={preview.columns} rows={preview.rows.slice(0, PREVIEW_ROWS)} empty={preview.empty} />
      <HistoryModal
        open={showAll}
        onOpenChange={onShowAllChange}
        title={preview.label}
        subtitle={modalSubtitle}
        tabs={[fullTab]}
        activeId={fullTab.id}
        onTabChange={() => undefined}
        loading={loading}
      />
    </SectionCard>
  );
}

/** Policies and loans unpaid this month. */
export function AttentionCard({ items, month }: { items: DashboardAttentionItem[]; month: string }) {
  const [showAll, setShowAll] = useState(false);
  const { data: all = [], isLoading } = useDashboardAttentionQuery(showAll);

  return (
    <ListCard
      preview={attentionTab(items)}
      fullTab={attentionTab(all)}
      description="Policies and loans unpaid this month"
      modalSubtitle={`Policies and loans unpaid in ${formatMonthKey(month)}`}
      loading={isLoading}
      showAll={showAll}
      onShowAllChange={setShowAll}
    />
  );
}

/** Latest policy, loan and balance payments. */
export function RecentTransactionsCard({ items }: { items: DashboardTransaction[] }) {
  const [showAll, setShowAll] = useState(false);
  const { data: all = [], isLoading } = useDashboardTransactionsQuery(showAll);

  return (
    <ListCard
      preview={transactionsTab(items)}
      fullTab={transactionsTab(all)}
      description="Latest policy, loan and balance payments across all members"
      modalSubtitle="Policy, loan and balance payments across all members"
      loading={isLoading}
      showAll={showAll}
      onShowAllChange={setShowAll}
    />
  );
}
